import { memo } from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from '@hooks';
import MetricItem from '@features/Analytics/Common/MetricItem';
import { formatFixedNumber, formatPercentNumberParts, isMissing } from '@helpers/formatNumbers';

const EMPTY_VALUE = { prefix: '', number: '—', suffix: '' };

export const MetricGrid = styled('div', { shouldForwardProp: prop => prop !== 'columns' })(({ columns }) => ({
    display: 'grid',
    gridGap: 16,
    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
    '@media (max-width: 1120px)': {
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    '@media (max-width: 600px)': {
        gridTemplateColumns: '100%',
    },
    marginBottom: 16,
}));

const formatRate = value => (isMissing(value) ? EMPTY_VALUE : formatPercentNumberParts(value, 'standard', 2));

const SocialRates = memo(({ data }) => {
    const { t } = useTranslation();
    const formatPoints = delta =>
        t('AnalyticsSocialPosts.percentagePoints', { value: formatFixedNumber(delta, 2) ?? String(delta) });

    return (
        <>
            <MetricGrid columns={4}>
                <MetricItem
                    title={t('AnalyticsSocialPosts.followers')}
                    data={{ metric: data?.followers, delta: data?.followersDelta }}
                    withPercentageDelta
                />
                <MetricItem
                    title={t('AnalyticsSocialPosts.reach28')}
                    data={{ metric: data?.reach28, delta: data?.reach28Delta }}
                    withPercentageDelta
                />
                <MetricItem
                    title={t('AnalyticsSocialPosts.rateByReach')}
                    data={{ metric: data?.rateByReach, delta: data?.rateByReachDelta }}
                    value={formatRate(data?.rateByReach)}
                    formatDelta={formatPoints}
                />
                <MetricItem
                    title={t('AnalyticsSocialPosts.rateByFollowers')}
                    data={{ metric: data?.rateByFollowers, delta: data?.rateByFollowersDelta }}
                    value={formatRate(data?.rateByFollowers)}
                    formatDelta={formatPoints}
                />
            </MetricGrid>
            <MetricGrid columns={5}>
                <MetricItem
                    title={t('AnalyticsSocialPosts.posts')}
                    data={{ metric: data?.posts, delta: data?.postsDelta }}
                    withPercentageDelta
                />
                <MetricItem
                    title={t('AnalyticsSocialPosts.reach')}
                    data={{ metric: data?.reach, delta: data?.reachDelta }}
                    withPercentageDelta
                />
                <MetricItem
                    title={t('AnalyticsSocialPosts.likes')}
                    data={{ metric: data?.likes, delta: data?.likesDelta }}
                    withPercentageDelta
                />
                <MetricItem
                    title={t('AnalyticsSocialPosts.comments')}
                    data={{ metric: data?.comments, delta: data?.commentsDelta }}
                    withPercentageDelta
                />
                <MetricItem
                    title={t('AnalyticsSocialPosts.shares')}
                    data={{ metric: data?.shares, delta: data?.sharesDelta }}
                    withPercentageDelta
                />
            </MetricGrid>
        </>
    );
});

SocialRates.displayName = 'SocialRates';

export default SocialRates;
